import { useState } from 'react';
import GridLayout, { WidthProvider } from 'react-grid-layout';
import { ExploreWidget } from '@/components/explore-widget';
import { Page } from '@/components/page';
import { Toggle } from '@/components/toggle';
import { GridPanel } from '@/components/grid-panel';
import { CollectionWidget } from '@/components/collection-widget';
import { MediaViewerWidget } from '@/components/media-viewer-widget';
import { DetailsWidget } from '@/components/details-widget';
import { TreeStoreProvider } from '@/context/tree-context';
import { ObjectJsonWidget } from '@/components/object-json-widget';
import { LeafletMapWidget } from '@/components/leaflet-map-widget';
import { AuthorisedFunction } from '@/utilities/authorised-function';
import { useScreenId } from '@/hooks/use-screen-id';
import { useTabId } from '@/hooks/use-tab-id';

const layout = [
  { i: 'main', x: 0, y: 0, w: 40, h: 20 },
  { i: 'main_next', x: 0, y: 1, w: 40, h: 20 },
  { i: 'sidebar_left', x: 0, y: 2, w: 40, h: 20 },
  { i: 'sidebar', x: 20, y: 2, w: 20, h: 20 },
  { i: 'footer_top', x: 0, y: 3, w: 40, h: 10 },
  { i: 'footer', x: 0, y: 4, w: 40, h: 10 },
  { i: 'footer_middle', x: 0, y: 5, w: 40, h: 10 },
  { i: 'footer_bottom', x: 0, y: 6, w: 40, h: 10 },
  { i: 'sidebar_1', x: 20, y: 7, w: 20, h: 11 },
  { i: 'sidebar_1_left', x: 0, y: 7, w: 20, h: 11 },
  { i: 'sidebar_2_left', x: 0, y: 8, w: 20, h: 11 },
  { i: 'sidebar_2', x: 20, y: 8, w: 20, h: 11 },
  { i: 'sidebar_3', x: 20, y: 12, w: 20, h: 11 },
  { i: 'sidebar_4', x: 20, y: 12, w: 40, h: 20 },
  { i: 'sidebar_5', x: 0, y: 12, w: 20, h: 11 },
  { i: 'map', x: 0, y: 12, w: 40, h: 20 },
];

const AutoWidthGridLayout = WidthProvider(GridLayout);

export function ExploreDev() {
  const [isDraggable, setIsDraggable] = useState<boolean>(false);
  const tabId = useTabId();
  const screenId = useScreenId();
  const idPrefix = `${tabId}/${screenId}`;

  if (!tabId || !screenId) {
    return null;
  }

  return (
    <Page
      title="View Object"
      toolbar={
        <Toggle
          enabled={isDraggable}
          label="Enable drag"
          onChange={setIsDraggable}
        />
      }
    >
      <AutoWidthGridLayout
        className="layout"
        layout={layout}
        cols={40}
        rowHeight={30}
        isDraggable={isDraggable}
      >
        <GridPanel key="main">
          <TreeStoreProvider name="explore-a">
            <ExploreWidget id={`${idPrefix}/explorer`} />
          </TreeStoreProvider>
        </GridPanel>
        <GridPanel key="main_next">
          <ObjectJsonWidget id={`${idPrefix}/object-json`} />
        </GridPanel>
        <GridPanel key="sidebar_left">
          <MediaViewerWidget id={`${idPrefix}/media-viewer`} />
        </GridPanel>
        <GridPanel key="footer_top">
          <DetailsWidget id={`${idPrefix}/details`} />
        </GridPanel>
        <GridPanel key="footer">
          <CollectionWidget id={`${idPrefix}/collection`} />
        </GridPanel>
        {AuthorisedFunction(['developer']) && (
          <GridPanel key="map">
            <LeafletMapWidget id={`${idPrefix}/map`} />
          </GridPanel>
        )}
      </AutoWidthGridLayout>
    </Page>
  );
}
